import type {
  IGroupedFilterConfig,
  IArrayFilterConfig,
  IRangeConfig,
  IDateConfig,
  IBooleanConfig,
  IFlagConfig,
} from '@/creditflowresearch/ui/common/components/FilterTags';
import type { TFilterPayload } from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/useCorporateBondScreenerSidebarFilter';
import type { ICorporateBondScreenerFilterBoundaries } from '@/creditflowresearch/domain/vo/Bondscreener';

export const groupedConfigs: IGroupedFilterConfig<TFilterPayload>[] = [
  {
    key: 'regionsCountries',
    countKey: 'selectedCountryIds',
    resetKeys: ['includedRegionIds', 'includedCountryIds', 'selectedCountryIds'],
    label: 'Regions & Countries',
  },
  {
    key: 'industriesSubindustries',
    countKey: 'selectedSubindustryIds',
    resetKeys: ['includedIndustryIds', 'includedSubindustryIds', 'selectedSubindustryIds'],
    label: 'Industries & Subindustries',
  },
];

export const arrayConfigs: IArrayFilterConfig<TFilterPayload>[] = [
  { key: 'includedProductIds', label: 'Credit Product', pluralLabel: 'Credit Products' },
  { key: 'includedMarketIds', label: 'Market', pluralLabel: 'Markets' },
  { key: 'includedCurrencyIds', label: 'Currency', pluralLabel: 'Currencies' },
  { key: 'excludedCurrencyIds', label: 'Currency', pluralLabel: 'Currencies', isExclude: true },
  { key: 'includedStatusIds', label: 'Status', pluralLabel: 'Statuses' },
  { key: 'includedIssuerIds', label: 'Issuer', pluralLabel: 'Issuers' },
  { key: 'excludedIssuerIds', label: 'Issuer', pluralLabel: 'Issuers', isExclude: true },
  { key: 'includedParentIds', label: 'Parent', pluralLabel: 'Parents' },
  { key: 'excludedParentIds', label: 'Parent', pluralLabel: 'Parents', isExclude: true },
  { key: 'includedTickerCompanyIds', label: 'Ticker', pluralLabel: 'Tickers' },
  { key: 'excludedTickerCompanyIds', label: 'Ticker', pluralLabel: 'Tickers', isExclude: true },
  { key: 'includedRankingIds', label: 'Ranking', pluralLabel: 'Rankings' },
  { key: 'includedRatingGroupIds', label: 'Rating', pluralLabel: 'Ratings' },
  { key: 'excludedRatingGroupIds', label: 'Rating', pluralLabel: 'Ratings', isExclude: true },
  { key: 'includedRatingAgencyIds', label: 'Rating Agency', pluralLabel: 'Rating Agencies' },
  { key: 'excludedRatingAgencyIds', label: 'Rating Agency', pluralLabel: 'Rating Agencies', isExclude: true },
  { key: 'includedCouponTypeIds', label: 'Coupon Type', pluralLabel: 'Coupon Types' },
  { key: 'includedRegistrationGroupIds', label: 'Registration', pluralLabel: 'Registrations' },
  { key: 'includedBenchmarkGroupIds', label: 'Benchmark', pluralLabel: 'Benchmarks' },
  { key: 'includedLeadIds', label: 'Lead', pluralLabel: 'Leads' },
  { key: 'excludedLeadIds', label: 'Lead', pluralLabel: 'Leads', isExclude: true },
  { key: 'includedLeadRoleIds', label: 'Lead Role', pluralLabel: 'Lead Roles' },
  { key: 'includedEsgIds', label: 'ESG' },
  { key: 'includedUseOfProceedsIds', label: 'Use of Proceeds' },
  { key: 'excludedUseOfProceedsIds', label: 'Use of Proceeds', isExclude: true },
  { key: 'includedListingIds', label: 'Listing', pluralLabel: 'Listings' },
  { key: 'includedAdditionalFilters', label: 'Additional Filter', pluralLabel: 'Additional Filters' },
  { key: 'excludedAdditionalFilters', label: 'Additional Filter', pluralLabel: 'Additional Filters', isExclude: true },
];

export const rangeConfigs: IRangeConfig<TFilterPayload, ICorporateBondScreenerFilterBoundaries>[] = [
  { key: 'includedDealSize', fromKey: 'includedDealSizeFrom', toKey: 'includedDealSizeTo', boundaryKey: 'dealSize', label: 'Deal Size' },
  { key: 'includedTrancheSize', fromKey: 'includedTrancheSizeFrom', toKey: 'includedTrancheSizeTo', boundaryKey: 'trancheSize', label: 'Tranche Size' },
  { key: 'includedTenor', fromKey: 'includedTenorFrom', toKey: 'includedTenorTo', boundaryKey: 'tenor', label: 'Tenor' },
  { key: 'excludedTenor', fromKey: 'excludedTenorFrom', toKey: 'excludedTenorTo', boundaryKey: 'tenor', label: 'Tenor', isExclude: true },
  { key: 'includedPricingSpread', fromKey: 'includedPricingSpreadFrom', toKey: 'includedPricingSpreadTo', boundaryKey: 'spread', label: 'Spread' },
  { key: 'includedCoupon', fromKey: 'includedCouponFrom', toKey: 'includedCouponTo', boundaryKey: 'coupon', label: 'Coupon' },
  { key: 'includedIssuePrice', fromKey: 'includedIssuePriceFrom', toKey: 'includedIssuePriceTo', boundaryKey: 'issuePrice', label: 'Issue Price' },
  { key: 'includedYield', fromKey: 'includedYieldFrom', toKey: 'includedYieldTo', boundaryKey: 'yield', label: 'Yield' },
  { key: 'includedXCovered', fromKey: 'includedXCoveredFrom', toKey: 'includedXCoveredTo', boundaryKey: 'xCovered', label: 'X Covered' },
  { key: 'includedIptPxd', fromKey: 'includedIptPxdFrom', toKey: 'includedIptPxdTo', boundaryKey: 'iptPxd', label: 'IPT PxD' },
  { key: 'includedAttrition', fromKey: 'includedAttritionFrom', toKey: 'includedAttritionTo', boundaryKey: 'attrition', label: 'Attrition' },
];

export const dateConfigs: IDateConfig<TFilterPayload, ICorporateBondScreenerFilterBoundaries>[] = [
  { key: 'date', fromKey: 'dateFrom', toKey: 'dateTo', label: 'Date', boundaryKey: 'date' },
  { key: 'includedMaturityDate', fromKey: 'includedMaturityDateFrom', toKey: 'includedMaturityDateTo', label: 'Maturity Date', boundaryKey: 'maturityDate' },
  { key: 'includedCallDate', fromKey: 'includedCallDateFrom', toKey: 'includedCallDateTo', label: 'Call Date', boundaryKey: 'callDate' },
];

export const booleanConfigs: IBooleanConfig<TFilterPayload>[] = [
  { key: 'dropped', label: 'Dropped' },
  { key: 'callable', label: 'Callable' },
  { key: 'perpetual', label: 'Perpetual' },
  { key: 'issuedByHoldingCompany', label: 'Holding Company' },
  { key: 'issuedByOperatingCompany', label: 'Operating Company' },
];

export const flagConfigs: IFlagConfig<TFilterPayload>[] = [
  { key: 'includeUsIgNonCountables', label: 'Include Non-Countables (US IG only)' },
];
